// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Bygning shell's keyboard map (v8 prototype): ⌘K/Ctrl+K palette from
 * anywhere; J/K (and →/←) next/previous in the mode's list (the strip, or
 * Gennemgang's blocking types and Rapporter & indberetning's tree, which have
 * none); G godkend and A afvis
 * in Ressourcer and Gennemgang; Esc steps back out of the current focus;
 * Enter opens the focused item. Classified by `app/keyTargets` kinds, so the
 * letter keys never fire while typing and Enter/Space stay with a focused
 * button. Pure: no React, no DOM.
 */

import type { TargetKind } from '../app/keyTargets';
import type { ModeId } from './modes';
import type { Selection } from './selection';

export type ShellKeyAction = 'palette' | 'next' | 'prev' | 'approve' | 'reject' | 'escape' | 'open';

export interface ShellKey {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  /** `kindOf(event.target)`. */
  kind: TargetKind;
  mode: ModeId;
  /** The palette handles its own keys while open (except ⌘K, which closes it). */
  paletteOpen: boolean;
}

/** Modes where J/K step through the strip. */
const STEPPING: ReadonlySet<ModeId> = new Set(['ressourcer', 'gennemgang', 'miljoe', 'overblik', 'rapport']);
/** Modes where G/A review the type in focus. */
const REVIEWING: ReadonlySet<ModeId> = new Set(['ressourcer', 'gennemgang']);

export function shellKeyAction(k: ShellKey): ShellKeyAction | null {
  if ((k.metaKey || k.ctrlKey) && !k.altKey && k.key.toLowerCase() === 'k') return 'palette';
  if (k.paletteOpen) return null;
  if (k.metaKey || k.ctrlKey || k.altKey) return null;
  // A text field or a select owns every key, Esc included (R10: revert).
  if (k.kind === 'text' || k.kind === 'choice') return null;
  if (k.key === 'Escape') return 'escape';
  // Enter on a button or link is its own activation.
  if (k.key === 'Enter') return k.kind === 'other' ? 'open' : null;
  const key = k.key.toLowerCase();
  if (STEPPING.has(k.mode)) {
    if (key === 'j') return 'next';
    if (key === 'k') return 'prev';
    // Arrows only off controls: a focused control may use them itself.
    if (k.kind === 'other' && k.key === 'ArrowRight') return 'next';
    if (k.kind === 'other' && k.key === 'ArrowLeft') return 'prev';
  }
  if (REVIEWING.has(k.mode)) {
    if (key === 'g') return 'approve';
    if (key === 'a') return 'reject';
  }
  return null;
}

/**
 * What Esc undoes, innermost first: the Sagsdata editor, the evidence tile,
 * the picked parts, Rediger's selected element, the focused part, the room
 * focus, the storey focus. Null
 * when there is nothing to step out of.
 */
export function escapePatch(sel: Selection): Partial<Selection> | null {
  if (sel.edit !== null) return { edit: null };
  if (sel.ev !== null) return { ev: null };
  if (sel.selParts.length > 0) return { selParts: [] };
  if (sel.element !== null) return { element: null };
  if (sel.partCode !== null) return { partCode: null };
  if (sel.room !== null) return { room: null };
  if (sel.storey !== null) return { storey: null };
  return null;
}
